// src/pages/Accounts.jsx
import React, { useState } from 'react'
import { useApp } from '../context/AppContext'
import { EmptyState, ConfirmModal, SectionHead, MetricsGrid, MetricCard } from '../components/UI'
import { ACCOUNT_TYPES, ACCOUNT_COLORS } from '../utils/constants'
import { fmt } from '../utils/helpers'

const emptyForm = () => ({ name: '', type: 'Bank', balance: '', color: '#185fa5' })

export default function Accounts() {
  const { accounts, addAccount, updateAccount, deleteAccount, totalAssets, totalLiab } = useApp()
  const [form,    setForm]    = useState(emptyForm())
  const [editId,  setEditId]  = useState(null)
  const [delId,   setDelId]   = useState(null)
  const [formErr, setFormErr] = useState('')

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const openEdit = (a) => {
    setForm({ name: a.name, type: a.type, balance: String(a.balance), color: a.color })
    setEditId(a.id)
    setFormErr('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const cancelEdit = () => { setEditId(null); setForm(emptyForm()); setFormErr('') }

  const save = () => {
    if (!form.name.trim()) { setFormErr('Enter account name'); return }
    if (accounts.some(a => a.name.toLowerCase() === form.name.trim().toLowerCase() && a.id !== editId)) {
      setFormErr('Account name already exists'); return
    }
    const data = { name: form.name.trim(), type: form.type, balance: parseFloat(form.balance) || 0, color: form.color }
    if (editId) updateAccount(editId, data)
    else        addAccount(data)
    setEditId(null)
    setForm(emptyForm())
    setFormErr('')
  }

  const typeIcon = { Cash: 'ti-cash', Bank: 'ti-building-bank', 'Credit Card': 'ti-credit-card', Savings: 'ti-piggy-bank', Investment: 'ti-chart-candlestick', Wallet: 'ti-wallet' }

  return (
    <>
      <MetricsGrid>
        <MetricCard label="Total accounts"  value={String(accounts.length)}    color="var(--gray-800)" />
        <MetricCard label="Total assets"    value={`₹${fmt(totalAssets)}`}     color="var(--brand-600)" />
        <MetricCard label="Liabilities"     value={`₹${fmt(totalLiab)}`}       color="var(--red-600)" />
        <MetricCard label="Net worth"       value={`₹${fmt(totalAssets - totalLiab)}`} color={totalAssets - totalLiab >= 0 ? 'var(--green-600)' : 'var(--red-600)'} />
      </MetricsGrid>

      {/* Form */}
      <div className="card" style={{ marginBottom: 14 }}>
        <SectionHead icon="ti-credit-card" title={editId ? 'Edit account' : 'Add account'} />
        <div className="form-grid">
          <div className="field">
            <label className="field-label">Account name</label>
            <input className="input" value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. SBI Savings" />
          </div>
          <div className="field">
            <label className="field-label">Type</label>
            <select className="select" value={form.type} onChange={e => upd('type', e.target.value)}>
              {ACCOUNT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Opening balance (₹)</label>
            <input className="input" type="number" value={form.balance} onChange={e => upd('balance', e.target.value)} placeholder="0.00" style={{ textAlign: 'right' }} />
          </div>
          <div className="field">
            <label className="field-label">Color</label>
            <div style={{ display: 'flex', gap: 7, alignItems: 'center', paddingTop: 5 }}>
              {ACCOUNT_COLORS.slice(0, 7).map(c => (
                <div key={c} onClick={() => upd('color', c)} style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer', border: `3px solid ${form.color === c ? 'var(--gray-900)' : 'transparent'}`, transition: 'transform .1s', flexShrink: 0 }} />
              ))}
            </div>
          </div>
        </div>
        {formErr && (
          <div style={{ color: 'var(--red-600)', fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <i className="ti ti-alert-circle" aria-hidden="true" />{formErr}
          </div>
        )}
        <div className="btn-row">
          <button className="btn btn-primary" onClick={save}>
            <i className="ti ti-check" aria-hidden="true" /> {editId ? 'Update' : 'Add'} account
          </button>
          {editId && <button className="btn btn-danger" onClick={cancelEdit}><i className="ti ti-x" aria-hidden="true" /> Cancel</button>}
        </div>
      </div>

      {/* Account list */}
      <div className="table-wrap">
        <div className="table-toolbar">
          <span className="table-toolbar-title">Accounts ({accounts.length})</span>
        </div>
        {accounts.length === 0 ? (
          <EmptyState icon="ti-credit-card" message="No accounts yet" sub="Add your first account above" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Account name</th>
                  <th style={{ width: 110 }}>Type</th>
                  <th style={{ width: 130, textAlign: 'right' }}>Balance (₹)</th>
                  <th style={{ width: 120, textAlign: 'right' }}>Normal bal.</th>
                  <th style={{ width: 68 }}></th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(a => (
                  <tr key={a.id}>
                    <td><span style={{ width: 12, height: 12, borderRadius: '50%', background: a.color, display: 'inline-block' }} /></td>
                    <td style={{ fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <i className={`ti ${typeIcon[a.type] || 'ti-credit-card'}`} style={{ fontSize: 15, color: a.color }} aria-hidden="true" />
                        {a.name}
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: 'var(--brand-50)', color: 'var(--brand-800)', fontWeight: 600 }}>{a.type}</span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: a.balance >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>
                      ₹{fmt(Math.abs(a.balance))}
                      <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 4, color: 'var(--gray-400)' }}>{a.balance >= 0 ? 'Dr' : 'Cr'}</span>
                    </td>
                    <td style={{ textAlign: 'right', color: ['Bank','Cash','Savings','Investment'].includes(a.type) ? 'var(--brand-600)' : 'var(--red-600)', fontWeight: 600, fontSize: 12 }}>
                      {['Bank','Cash','Savings','Investment'].includes(a.type) ? 'Debit' : 'Credit'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button className="icon-btn" onClick={() => openEdit(a)} aria-label="Edit account"><i className="ti ti-edit" aria-hidden="true" /></button>
                        <button className="icon-btn danger" onClick={() => setDelId(a.id)} aria-label="Delete account"><i className="ti ti-trash" aria-hidden="true" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!delId}
        onClose={() => setDelId(null)}
        onConfirm={() => deleteAccount(delId)}
        title="Delete account"
        message="This will permanently delete the account. Existing transactions referencing it will be unaffected."
        confirmLabel="Delete"
      />
    </>
  )
}
